// theme.js — User theme controller for GDBS.
//
// Hooks the Flux design tokens (in css/flux/flux.css) into the existing
// app.css color variables so a user can pick dark/light, accent hue,
// font, density, and corner radius from a Settings drawer. Preferences
// persist to localStorage and apply before first paint via a small
// inline bootstrap in index.html.
//
// Two complementary surfaces:
//   1. data-* attributes on <html> drive the Flux variables (--surface-*,
//      --text-*, --accent, etc.) where Flux is active.
//   2. The bridge stylesheet css/theme-bridge.css maps Flux variables to
//      the existing app.css variables, so dark/light flip propagates
//      through every legacy panel without rewriting it.

const STORAGE_KEY = 'gdbs_theme_v1';

const DEFAULTS = {
  theme: 'light',         // dark | light (matches index.html inline bootstrap;
                          //  app panels that need dark are hardcoded-dark, so
                          //  flipping the default to light is safe and keeps
                          //  marketing/landing rendering consistent across
                          //  first visits and stored-preference visits)
  font: 'system',         // dm-sans | outfit | satoshi | space | cabinet | geist | jetbrains | system
  radius: 'soft',         // sharp | soft | round | pill
  density: 'normal',      // compact | normal | spacious
  accentHue: 250,         // 0-360 (blue default; cyan=195, sky=220, blue=250, indigo=275 per flux palette)
  animSpeed: 1,           // 0-2
  shadowIntensity: 1,     // 0-2
};

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(prefs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch {}
}

function apply(prefs) {
  const html = document.documentElement;
  html.setAttribute('data-theme', prefs.theme);
  html.setAttribute('data-font', prefs.font);
  html.setAttribute('data-radius', prefs.radius);
  html.setAttribute('data-density', prefs.density);
  html.style.setProperty('--accent-hue', prefs.accentHue);
  html.style.setProperty('--anim-speed', prefs.animSpeed);
  html.style.setProperty('--shadow-intensity', prefs.shadowIntensity);
  // Broadcast to anyone caching theme-derived values (Chart.js defaults,
  // chart-theme.js redraw-all hook, custom widgets that snapshot CSS vars
  // at construct time). Fires after the data-* attrs are set so listeners
  // re-reading CSS vars see the new theme's resolved values.
  try {
    window.dispatchEvent(new CustomEvent('gdbs:theme-changed', { detail: { ...prefs } }));
  } catch (_) {}
}

// Apply immediately to avoid flash of wrong theme.
const _prefs = load();
apply(_prefs);

// ── Settings drawer UI ──────────────────────────────────────────

function ensureDrawer() {
  let drawer = document.getElementById('theme-drawer');
  if (drawer) return drawer;
  drawer = document.createElement('aside');
  drawer.id = 'theme-drawer';
  drawer.className = 'theme-drawer';
  drawer.innerHTML = `
    <div class="theme-drawer-head">
      <span><i class="fas fa-palette"></i> Theme & Appearance</span>
      <button class="theme-drawer-close" aria-label="Close">&times;</button>
    </div>
    <div class="theme-drawer-body">
      <div class="theme-group">
        <label class="theme-label">Mode</label>
        <div class="theme-opts" data-key="theme">
          <button class="theme-opt" data-val="dark">Dark</button>
          <button class="theme-opt" data-val="light">Light</button>
        </div>
      </div>
      <div class="theme-group">
        <label class="theme-label">Font</label>
        <div class="theme-opts" data-key="font">
          <button class="theme-opt" data-val="system">System</button>
          <button class="theme-opt" data-val="dm-sans">DM Sans</button>
          <button class="theme-opt" data-val="outfit">Outfit</button>
          <button class="theme-opt" data-val="space">Space Grotesk</button>
          <button class="theme-opt" data-val="geist">Geist</button>
          <button class="theme-opt" data-val="jetbrains">JetBrains Mono</button>
        </div>
      </div>
      <div class="theme-group">
        <label class="theme-label">Corner radius</label>
        <div class="theme-opts" data-key="radius">
          <button class="theme-opt" data-val="sharp">Sharp</button>
          <button class="theme-opt" data-val="soft">Soft</button>
          <button class="theme-opt" data-val="round">Round</button>
          <button class="theme-opt" data-val="pill">Pill</button>
        </div>
      </div>
      <div class="theme-group">
        <label class="theme-label">Density</label>
        <div class="theme-opts" data-key="density">
          <button class="theme-opt" data-val="compact">Compact</button>
          <button class="theme-opt" data-val="normal">Normal</button>
          <button class="theme-opt" data-val="spacious">Spacious</button>
        </div>
      </div>
      <div class="theme-group">
        <label class="theme-label">Accent hue <span id="theme-hue-val" class="theme-val"></span></label>
        <div class="theme-hue-bar"></div>
        <input type="range" min="0" max="360" step="1" id="theme-hue-slider" class="theme-slider">
      </div>
      <div class="theme-group">
        <label class="theme-label">Animation speed <span id="theme-anim-val" class="theme-val"></span></label>
        <input type="range" min="0" max="2" step="0.1" id="theme-anim-slider" class="theme-slider">
      </div>
      <div class="theme-group">
        <label class="theme-label">Shadow intensity <span id="theme-shadow-val" class="theme-val"></span></label>
        <input type="range" min="0" max="2" step="0.1" id="theme-shadow-slider" class="theme-slider">
      </div>
      <div class="theme-drawer-footer">
        <button class="btn btn-sm" id="theme-reset">Reset to defaults</button>
      </div>
    </div>
  `;
  document.body.appendChild(drawer);
  wireDrawer(drawer);
  return drawer;
}

function syncDrawer(prefs) {
  const drawer = document.getElementById('theme-drawer');
  if (!drawer) return;
  drawer.querySelectorAll('.theme-opts').forEach(group => {
    const key = group.dataset.key;
    group.querySelectorAll('.theme-opt').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.val === prefs[key]);
    });
  });
  const hue = drawer.querySelector('#theme-hue-slider');
  if (hue) hue.value = prefs.accentHue;
  const huv = drawer.querySelector('#theme-hue-val');
  if (huv) huv.textContent = `${prefs.accentHue}°`;
  const anim = drawer.querySelector('#theme-anim-slider');
  if (anim) anim.value = prefs.animSpeed;
  const animv = drawer.querySelector('#theme-anim-val');
  if (animv) animv.textContent = prefs.animSpeed.toFixed(1);
  const sh = drawer.querySelector('#theme-shadow-slider');
  if (sh) sh.value = prefs.shadowIntensity;
  const shv = drawer.querySelector('#theme-shadow-val');
  if (shv) shv.textContent = prefs.shadowIntensity.toFixed(1);
}

function wireDrawer(drawer) {
  drawer.querySelector('.theme-drawer-close')?.addEventListener('click', closeDrawer);
  drawer.querySelectorAll('.theme-opts').forEach(group => {
    const key = group.dataset.key;
    group.querySelectorAll('.theme-opt').forEach(btn => {
      btn.addEventListener('click', () => {
        _prefs[key] = btn.dataset.val;
        save(_prefs); apply(_prefs); syncDrawer(_prefs);
        window.gdbsToastInfo?.(`${key} → ${btn.dataset.val}`, { timeoutMs: 2200 });
      });
    });
  });
  drawer.querySelector('#theme-hue-slider')?.addEventListener('input', (e) => {
    _prefs.accentHue = parseInt(e.target.value, 10);
    save(_prefs); apply(_prefs); syncDrawer(_prefs);
  });
  drawer.querySelector('#theme-anim-slider')?.addEventListener('input', (e) => {
    _prefs.animSpeed = parseFloat(e.target.value);
    save(_prefs); apply(_prefs); syncDrawer(_prefs);
  });
  drawer.querySelector('#theme-shadow-slider')?.addEventListener('input', (e) => {
    _prefs.shadowIntensity = parseFloat(e.target.value);
    save(_prefs); apply(_prefs); syncDrawer(_prefs);
  });
  drawer.querySelector('#theme-reset')?.addEventListener('click', () => {
    Object.assign(_prefs, DEFAULTS);
    save(_prefs); apply(_prefs); syncDrawer(_prefs);
    window.gdbsToastSuccess?.('Theme reset to defaults', { timeoutMs: 2200 });
  });
}

export function openDrawer() {
  const drawer = ensureDrawer();
  syncDrawer(_prefs);
  drawer.classList.add('open');
  document.body.classList.add('theme-drawer-open');
}

export function closeDrawer() {
  document.getElementById('theme-drawer')?.classList.remove('open');
  document.body.classList.remove('theme-drawer-open');
}

// Public toggle for the topbar button.
window.gdbsToggleThemeDrawer = function() {
  const drawer = document.getElementById('theme-drawer');
  if (drawer && drawer.classList.contains('open')) closeDrawer();
  else openDrawer();
};

// Allow ESC to close.
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeDrawer();
});

export { _prefs, apply, save, load };
